export const LOCATION_MAX_CHARS = 120;

export const LOCATION_INVALID_MSG = 'Enter a valid city or location.';

const ALLOWED_CHARS =
  /^[A-Za-z][A-Za-z0-9\s,.'()\u2012\u2013\u2014\u2015\u2212-]*$/i;

/**
 * Strip characters that are never valid in a work location.
 */
export function sanitizeLocationInput(raw) {
  return String(raw ?? '').replace(/[^A-Za-z0-9\s,.'()\u2012\u2013\u2014\u2015\u2212-]/g, '');
}

/**
 * @returns {string|undefined} error message or undefined when valid
 */
export function validateLocationField(value) {
  const t = String(value ?? '').trim();
  if (!t) return 'Location is required.';
  if (t.length > LOCATION_MAX_CHARS) {
    return `Location must be ${LOCATION_MAX_CHARS} characters or less.`;
  }
  if (!/[A-Za-z]/.test(t)) return LOCATION_INVALID_MSG;
  if (!ALLOWED_CHARS.test(t)) return LOCATION_INVALID_MSG;

  const tokens = t.split(/[\s,]+/).filter(Boolean);
  if (tokens.length === 0) return LOCATION_INVALID_MSG;

  let letterWordCount = 0;
  let numericTokenCount = 0;

  for (const token of tokens) {
    const lettersOnly = token.replace(/[^A-Za-z]/g, '');
    const hasLetter = lettersOnly.length > 0;
    const hasDigit = /\d/.test(token);

    // Reject glued letter+digit tokens (e.g. gvhhchd12, uytrfds98765).
    if (hasLetter && hasDigit) {
      return LOCATION_INVALID_MSG;
    }

    if (/^\d+$/.test(token)) {
      numericTokenCount += 1;
      // Optional postcode: one block of 4–8 digits only.
      if (numericTokenCount > 1 || token.length < 4 || token.length > 8) {
        return LOCATION_INVALID_MSG;
      }
      continue;
    }

    if (!hasLetter || lettersOnly.length < 2) {
      return LOCATION_INVALID_MSG;
    }

    if (!/^[A-Za-z'.()-]+$/.test(token)) {
      return LOCATION_INVALID_MSG;
    }

    // Long consonant-only strings are unlikely place names.
    if (lettersOnly.length >= 5 && !/[aeiouAEIOU]/i.test(lettersOnly)) {
      return LOCATION_INVALID_MSG;
    }

    letterWordCount += 1;
  }

  if (letterWordCount === 0) {
    return LOCATION_INVALID_MSG;
  }

  return undefined;
}

export function isValidLocationField(value) {
  return validateLocationField(value) === undefined;
}
